import { Anchor, BoxProps, Button, Flex, Heading, Image, palette, Stack, Tag, TagText, Text } from "@govlaunch/web";
import React, { ReactElement, ReactNode, useState } from "react";
import { FormattedMessage } from "react-intl";
import { IRelatedStoriesFieldsFragment } from "../fragments/__generated__/RelatedStoriesFieldsFragment.generated";
import { IStoryFieldsFragment } from "../fragments/__generated__/StoryFieldsFragment.generated";
import { IMediaType } from "../graphql/types";
import { ArrowDownIcon } from "./ArrowIcons";
import { StoryAuthorOneLineCard, TCoauthor } from "./StoryAuthor";

interface IStoryRelatedStoriesSmallProps extends BoxProps {
  relatedStories: IRelatedStoriesFieldsFragment[];
  placeholderImageSrc?: string;
  renderLinkWrap?: (story: IRelatedStoriesFieldsFragment, children: ReactNode) => ReactNode;
  renderTagLinkWrap?: (tag: NonNullable<IStoryFieldsFragment["tags"]>[0], children: ReactNode) => ReactNode;
  renderAuthorLinkWrap?: (story: Pick<IStoryFieldsFragment, "author">, children: ReactElement) => ReactElement;
}

export default function StoryRelatedStoriesSmall({
  relatedStories,
  renderLinkWrap,
  placeholderImageSrc,
  renderTagLinkWrap,
  renderAuthorLinkWrap,
}: IStoryRelatedStoriesSmallProps) {
  const { items, showingMore, toggle } = useShowMore({
    items: relatedStories,
    initialCount: 2,
  });

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <>
      <Heading as="h2" color="gray.900" fontWeight="bold" fontSize="2xl" mb={1}>
        <FormattedMessage defaultMessage="Related Stories" id="nuwuqz" />
      </Heading>
      <div css={{ paddingLeft: 10 }}>
        <Stack direction="column" mt={3} shouldWrapChildren={true}>
          {items.map((relatedStory, index) => {
            const storyCoverImage = (relatedStory.medias || []).find(({ type }) => type === IMediaType.Image);
            const authorChildren = (
              <StoryAuthorOneLineCard author={relatedStory.author as TCoauthor} fontSize={14} flexWrap="wrap" />
            );
            const children = (
              <div
                css={{ borderTop: index === 0 ? "none" : "1px solid", borderColor: palette.gray[100], paddingTop: 10 }}
              >
                {renderAuthorLinkWrap ? renderAuthorLinkWrap(relatedStory, authorChildren) : authorChildren}
                <Anchor key={relatedStory._id} d="block" borderColor="gray.100">
                  <div css={{ display: "flex", marginBottom: 15, marginTop: 15 }}>
                    {storyCoverImage && (
                      <Image
                        alt="story cover image"
                        src={`https://imaginary-ssl.govlaunch.com/resize?quality=100&compression=0&width=326&height=163&url=${storyCoverImage.url}`}
                        borderRadius="md"
                        width={50}
                        height={50}
                        mr={3}
                      />
                    )}

                    {!storyCoverImage && placeholderImageSrc && (
                      <Image alt="story cover placeholder image" src={placeholderImageSrc} maxWidth={80} mr={4} />
                    )}

                    <Text
                      as="h4"
                      color="blue.600"
                      css={{ fontSize: 17, fontWeight: 700 }}
                      clamp={2}
                      title={relatedStory.title}
                    >
                      {relatedStory.title}
                    </Text>
                  </div>
                </Anchor>
                <div>
                  {relatedStory.tags.map((tag) => {
                    const children = (
                      <Tag mr={2} mb={2} key={tag._id} color="black" title={tag.name}>
                        <TagText>{tag.name}</TagText>
                      </Tag>
                    );
                    return renderTagLinkWrap ? renderTagLinkWrap(tag, children) : children;
                  })}
                </div>
              </div>
            );

            return renderLinkWrap ? renderLinkWrap(relatedStory, children) : children;
          })}

          {relatedStories.length > 2 && !showingMore && (
            <Flex justify="center" mt={4}>
              <Button
                variant="secondary"
                size="sm"
                onClick={toggle}
                rightIcon={(props) => <ArrowDownIcon {...props} />}
              >
                <FormattedMessage defaultMessage="Show more" id="aWpBzj" />
              </Button>
            </Flex>
          )}
        </Stack>
      </div>
    </>
  );
}

interface IUseShowMoreArgs<T> {
  items: Array<T>;
  initialCount: number;
}

interface IUseShowMore<T> {
  items: Array<T>;
  showingMore: boolean;
  toggle: (arg?: any) => void;
}

function useShowMore<T>({ items, initialCount = 2 }: IUseShowMoreArgs<T>): IUseShowMore<T> {
  const [isShowingMore, setShowMore] = useState<boolean>(false);

  if (isShowingMore) {
    return {
      items,
      showingMore: true,
      toggle: () => setShowMore(false),
    };
  }

  return {
    items: items.slice(0, initialCount),
    showingMore: false,
    toggle: () => setShowMore(true),
  };
}
